import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  // SEC-F0.3 — MFA opcional (TOTP)
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
} from 'firebase/auth'
import {
  doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs,
  serverTimestamp, writeBatch, deleteDoc, addDoc,
} from 'firebase/firestore'
import { auth, db, googleProvider, functions, diagnosticoAppCheck } from '@core/lib/firebase'
import { DEV_SIN_KEY, DEPLOY_SIN_KEY } from '@core/lib/appCheckDiagnostico'
import { httpsCallable } from 'firebase/functions'
import { ALLOWED_DOMAIN, AUTH_CONFIG_DEFAULT, COLLECTIONS, RECURSO_TIPOS, USER_TIPOS } from '@core/lib/constants'
import { docConId } from '@core/lib/docConId'
import { hdParaGoogle, normalizarAuthConfig } from '@core/hooks/useAuthConfig'
import { estadoBloqueoPorEmpresa } from '@core/lib/estadoEmpresa'
// ── v2 — modelo formal de permisos (Usuarios/Roles/Asignaciones) ─────
// Doc fundacional: docs/MODELO_ORGANIZACIONAL.md
// Plan: docs/PLAN_IMPLEMENTACION_USUARIOS_V2.md
import { permisosEfectivos as v2_permisosEfectivos, puedeImpersonar } from '@core/lib/permisos'
import { recursoPasaFiltros } from '@core/lib/filtrosPermiso'
import { cargarEjesEnCache } from '@core/lib/modulos'
import { limpiarCachesNombres } from '@core/hooks/useResolverNombres'
import { limpiarCacheDirectorioEmpresa } from '@core/hooks/useDirectorioEmpresa'
import { createLogger } from '@core/lib/logger'
import { esRolLadoMilbrands } from '@core/lib/ladoRol'
import { destinoTrasLogin } from '@core/lib/rutas'

// Logger de auth. log.error() persiste a `clientLogs` en prod — PERO solo si
// el usuario YA está autenticado (la rule de clientLogs exige isSignedIn() +
// uid == auth.uid). Los fallos de login (pre-auth) NO se persisten: quedan en
// consola y, sobre todo, se muestran en pantalla con el err.code visible
// (ver mensajeAuthError) para diagnóstico remoto sin DevTools.
const authLog = createLogger('Auth')

// ── App Check: decir en voz alta cuando NO se inicializó (report oz7FcUdw) ──
// `firebase.js` calcula el diagnóstico y lo deja como valor; el grito vive acá
// porque este archivo ya importa firebase Y el logger, y porque emitir desde
// `firebase.js` armaría un ciclo de imports (`logger.js` importa `db`/`auth`
// de ahí) además de chocar con el candado de `console.*`.
//
// Se avisa una sola vez por sesión —flag módulo-level, molde de
// `yaGritoSinCota` en `useDashboardData.js`— y DENTRO del primer
// `onAuthStateChanged`: ahí `logger.error` sí persiste a `clientLogs` cuando
// hay usuario (la rule exige `isSignedIn()`), que es el único canal con
// lectores reales (`ClientLogsTab`). Sin usuario igual queda la línea de
// consola, que es lo que hoy no existe.
//
// `info` es noop en prod (`logger.js`), o sea que el caso de dev local no
// ensucia nada deployado.
let yaAvisoAppCheck = false
function avisarAppCheckUnaVez() {
  if (yaAvisoAppCheck) return
  const { nivel, mensaje } = diagnosticoAppCheck || {}
  if (nivel !== DEPLOY_SIN_KEY && nivel !== DEV_SIN_KEY) return
  yaAvisoAppCheck = true
  if (nivel === DEPLOY_SIN_KEY) authLog.error(mensaje, null, { nivel })
  else authLog.info(mensaje, { nivel })
}

// Mapea un err.code de Firebase Auth a un mensaje legible para el usuario.
// - Devuelve null para "no-errores" (el usuario cerró el popup) → no mostramos
//   cartel.
// - Para códigos no mapeados, deja el code CRUDO entre paréntesis: así un
//   usuario que falla puede reportar el error exacto con solo mirar la pantalla,
//   sin abrir la consola del navegador.
function mensajeAuthError(err) {
  switch (err?.code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return null
    case 'auth/popup-blocked':
      return 'Tu navegador bloqueó la ventana de Google. Habilitá los popups para este sitio e intentá de nuevo.'
    case 'auth/unauthorized-domain':
      return 'Este dominio no está autorizado para iniciar sesión con Google. Avisale al equipo (código: auth/unauthorized-domain).'
    case 'auth/operation-not-allowed':
      return 'El inicio de sesión con Google no está habilitado en este entorno. Avisale al equipo (código: auth/operation-not-allowed).'
    case 'auth/network-request-failed':
      return 'Hubo un problema de conexión. Revisá tu internet e intentá de nuevo.'
    case 'auth/too-many-requests':
      return 'Demasiados intentos. Esperá unos minutos antes de volver a intentar.'
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Email o contraseña incorrectos.'
    case 'auth/user-not-found':
      return 'No existe una cuenta con ese email.'
    case 'auth/invalid-email':
      return 'El email no es válido.'
    default:
      return err?.code
        ? `No se pudo iniciar sesión (${err.code}).`
        : 'No se pudo iniciar sesión.'
  }
}

// Frontera staff/cliente (caso DDA, 27-ago-2026): la decisión vive en
// `src/core/lib/ladoRol.js` — catálogo `roles/{id}.lado` primero, la lista
// legacy como fallback. Acá sólo se re-exporta la lista por compatibilidad.
export { ROLES_STAFF_MILBRANDS } from '@core/lib/ladoRol'
// Roles que tienen poder de admin global (saltean checks).
export const ROLES_ADMIN_GLOBAL = ['WIZARD', 'OPERACIONES']

const AuthContext = createContext(null)

// ───────────────────────────────────────────────────────────────────
// Race-condition guard para canje de invitación con Google.
//
// Cuando el cliente activa con Google, signInWithPopup() resuelve y
// onAuthStateChanged dispara INMEDIATAMENTE. Pero todavía no terminamos
// de escribir el userDoc tipo='cliente' ni de migrar accesos/contactos.
// Si ensureUserDoc corre primero, cae al fallback de "crear consultor".
//
// Solución: registerWithInviteGoogle setea una Promise aquí ANTES de
// abrir el popup, y ensureUserDoc la awaitea antes de hacer cualquier
// lookup. Cuando termina la registración real, la Promise resuelve y
// ensureUserDoc encuentra el doc cliente recién creado.
// ───────────────────────────────────────────────────────────────────
let _registrationInFlight = null

// ── El registro lo escribe el SERVIDOR (ADR self-service D3, PR 3a) ──────
// `registrarUsuario` es idempotente por uid: crea `usuarios/{uid}` si no
// existe (tipo cliente, sin empresas, `emailVerificado` desde el token) o
// sincroniza `emailVerificado`. El navegador ya no escribe ese doc. Reintento
// corto porque el PRIMER callable tras un sign-in puede fallar con el token de
// App Check todavía no listo (mismo motivo que `vincularCuentaGoogle`).
/**
 * La URL absoluta a la que vuelve el mail de verificación (su `continueUrl`):
 * sólo una ruta interna, validada por el MISMO `destinoTrasLogin` que después
 * aplica la pantalla de acciones de Auth al leerla (`rutaDeContinueUrl`, A3).
 * Lo que ese validador rechazaría del otro lado, acá ni se manda.
 */
export function destinoDeVerificacion(volverA) {
  const ruta = destinoTrasLogin(volverA, null)
  if (!ruta) return null
  if (typeof window === 'undefined' || !window.location || !window.location.origin) return null
  return `${window.location.origin}${ruta}`
}

// ── Candado de verificación y reenvío del mail (Plataforma A3) ──────────────
// Regla de Mariano (25-sep): «siempre hay que verificar mails». El candado
// mira el TOKEN de Auth (`emailVerified`), no el doc: un doc sin el campo, o
// que no se pudo leer, no abre la puerta. Quien quedó afuera puede pedir otro
// mail con su contraseña (`reenviarVerificacion`), que para mandarlo tiene
// que iniciar sesión un instante: mientras dura, el candado no pisa nada.
export const MENSAJE_FALTA_VERIFICAR = 'Verificá tu email antes de iniciar sesión. Revisá tu bandeja de entrada.'
/** Espera mínima entre dos reenvíos del mail de verificación desde este navegador. */
export const ESPERA_ENTRE_REENVIOS_MS = 60_000
let _reenvioEnVuelo = null
let _ultimoReenvioMs = 0
/** Sólo para tests: el reloj del reenvío es de módulo. */
export function _reiniciarReenvioParaTests() { _reenvioEnVuelo = null; _ultimoReenvioMs = 0 }

async function llamarRegistrarUsuario(perfil) {
  let ultimo = null
  for (let intento = 0; intento < 3; intento++) {
    try {
      const r = await httpsCallable(functions, 'registrarUsuario')(perfil)
      return r?.data || {}
    } catch (e) {
      ultimo = e
      authLog.warn('registrarUsuario reintento', intento + 1, e?.message || e)
      await new Promise(r => setTimeout(r, 500))
    }
  }
  throw ultimo || new Error('registrarUsuario falló')
}

// ── Canje de invitación por código — lo hace el SERVIDOR (PR 3b) ──────────
const CLAVE_INVITACION_PENDIENTE = 'mb.invitacionPendiente'
function guardarInvitacionPendiente(codigo) {
  try { window.localStorage.setItem(CLAVE_INVITACION_PENDIENTE, codigo) } catch (e) { authLog.warn('sin storage para la invitación pendiente', e?.message || e) }
}
function leerInvitacionPendiente() {
  try { return window.localStorage.getItem(CLAVE_INVITACION_PENDIENTE) || null } catch { return null }
}
function borrarInvitacionPendiente() {
  try { window.localStorage.removeItem(CLAVE_INVITACION_PENDIENTE) } catch { /* sin storage: nada que borrar */ }
}
const MENSAJES_INVITACION = {
  'codigo-invalido': 'Código de invitación inválido',
  'codigo-usado': 'Este código ya fue utilizado o está inactivo',
  'codigo-vencido': 'El código ha expirado',
  'email-no-coincide': 'El email no coincide con el de la invitación',
  'invitacion-sin-email': 'Esta invitación no tiene un email asociado. Pedile una nueva a quien te invitó.',
  'email-no-verificado': 'Verificá tu email antes de activar la invitación.',
  'pre-doc-ajeno': 'La invitación apunta a una cuenta que no es tuya. Pedile una nueva a quien te invitó.',
  'usuario-con-privilegios': 'Esta cuenta no puede canjear invitaciones.',
}
const RECHAZOS_DEFINITIVOS = ['codigo-invalido', 'codigo-usado', 'codigo-vencido', 'email-no-coincide', 'invitacion-sin-email', 'pre-doc-ajeno', 'usuario-con-privilegios']
function codigoDeRechazo(err) {
  return err?.details?.codigo || err?.customData?.details?.codigo || null
}
function esRechazoDefinitivoDeInvitacion(err) {
  return RECHAZOS_DEFINITIVOS.includes(codigoDeRechazo(err))
}
function mensajeDeInvitacion(err) {
  return MENSAJES_INVITACION[codigoDeRechazo(err)] || 'No se pudo activar la invitación. Probá de nuevo en unos segundos.'
}
async function llamarAceptarInvitacion(codigo) {
  let ultimo = null
  for (let intento = 0; intento < 3; intento++) {
    try {
      const r = await httpsCallable(functions, 'aceptarInvitacion')({ codigo })
      return r?.data || {}
    } catch (e) {
      if (esRechazoDefinitivoDeInvitacion(e) || e?.code === 'functions/resource-exhausted') throw e
      ultimo = e
      authLog.warn('aceptarInvitacion reintento', intento + 1, e?.message || e)
      await new Promise(r => setTimeout(r, 500))
    }
  }
  throw ultimo || new Error('aceptarInvitacion falló')
}

function esCuentaDePassword(firebaseUser) {
  return (firebaseUser?.providerData || []).some(p => p?.providerId === 'password')
}

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)                   // Firebase Auth user
  const [userDoc, setUserDoc] = useState(null)              // Firestore user document (real)
  // v2 (#49 B9.4-b) — state legacy `accesos[]` removido. El alcance ahora vive
  // en /asignaciones y se materializa en rolDoc + asignacionesV2 + empresaIds[] denormalizado.
  // ── v2 — cache del rol y asignaciones del usuario logueado ────────
  // Se carga al login (después de ensureUserDoc) y se mantiene en sesión.
  // No se vuelve a tocar Firestore por render, todo se evalúa contra este cache.
  const [rolDoc, setRolDoc] = useState(null)                // doc /roles/{rolId} con permisos[]
  const [asignacionesV2, setAsignacionesV2] = useState([])  // docs /asignaciones del user (esActivo)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // A3 — el último login rebotó por el candado de verificación: LoginPage
  // ofrece reenviar el mail.
  const [faltaVerificar, setFaltaVerificar] = useState(false)

  // --- DEV-only impersonation ---
  // Permite a admin abrir el portal como otro usuario via ?impersonate=<uid>.
  // NO cambia la auth de Firebase: se sigue escribiendo con los permisos del admin real.
  //
  // IMPORTANTE: re-leemos el query param ante cualquier cambio de URL (navegación
  // dentro del SPA, popstate, pushState/replaceState). Si no lo hiciéramos, al
  // quedar el state "pegado" el portal seguiría usando el uid impersonado aunque
  // la URL ya no lo tenga.
  // Fase 1.6 — bloqueo / banner por estado de empresa
  // { bloqueado: bool, motivo?: string, nivel?: 'baja'|'suspension' }
  const [estadoBloqueo, setEstadoBloqueo] = useState({ bloqueado: false })

  // SEC-F0.3 — MFA pendiente: cuando el usuario tiene 2FA enrollado, Firebase
  // emite `auth/multi-factor-auth-required` y devuelve un resolver. El componente
  // de login (LoginPage) inspecciona este state para mostrar el prompt del código
  // de 6 dígitos. submitMfaCode() completa el sign-in.
  const [mfaResolver, setMfaResolver] = useState(null)

  // --- Rol simulado (role switcher para super admin) ---
  // #58 — permite previsualizar la app "como" otro rol sin impersonar un
  // usuario real. Reemplaza a la simulación por Vista.
  // rolSimulado: null | { rolId: string, tipo: 'milbrands'|'cliente'|'colaborador', empresaId?: string }
  const [rolSimulado, setRolSimulado] = useState(null)

  const [impersonated, setImpersonated] = useState(null)
  // Impersonación persistida POR PESTAÑA (sessionStorage). El `?impersonate=<uid>`
  // solo viaja en la URL del primer load; al navegar dentro del portal react-router
  // dropea el query param, y sin persistencia se perdía la impersonación (volvías al
  // admin). sessionStorage es por-pestaña: la pestaña "Ver como" sigue impersonando,
  // la del admin real no se toca. Fuente efectiva = URL (si está) ?? sessionStorage.
  const readImpersonateFromEnv = () => {
    if (typeof window === 'undefined') return null
    const fromUrl = new URLSearchParams(window.location.search).get('impersonate')
    if (fromUrl) {
      try { window.sessionStorage.setItem('mb_impersonate_uid', fromUrl) } catch { /* noop */ }
      return fromUrl
    }
    try { return window.sessionStorage.getItem('mb_impersonate_uid') } catch { return null }
  }
  const [impersonateUid, setImpersonateUid] = useState(readImpersonateFromEnv)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const readFromUrl = () => {
      const q = readImpersonateFromEnv()
      setImpersonateUid(prev => (prev === q ? prev : q))
    }
    // react-router usa history.pushState → escuchamos popstate y además
    // monkey-patch de pushState/replaceState para captar navegaciones SPA.
    window.addEventListener('popstate', readFromUrl)
    const origPush = window.history.pushState
    const origReplace = window.history.replaceState
    window.history.pushState = function (...args) { origPush.apply(this, args); readFromUrl() }
    window.history.replaceState = function (...args) { origReplace.apply(this, args); readFromUrl() }
    // Primera lectura por si la URL cambió entre el useState inicial y el mount
    readFromUrl()
    return () => {
      window.removeEventListener('popstate', readFromUrl)
      window.history.pushState = origPush
      window.history.replaceState = origReplace
    }
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      avisarAppCheckUnaVez()
      if (firebaseUser) {
        // A3 — candado de verificación, fail-CLOSED y por el TOKEN, ANTES de
        // tocar ningún doc (ensureUserDoc migra/repara por mail: nada de eso
        // corre para una casilla no probada). Un registro o un reenvío en
        // vuelo terminan primero: son los que mandan el mail y cierran sesión.
        if (!firebaseUser.emailVerified) {
          if (_registrationInFlight) { try { await _registrationInFlight } catch { /* catch-mudo: sólo se espera el fin; el error del registro lo sube registrarConEmail */ } }
          if (_reenvioEnVuelo) {
            try { await _reenvioEnVuelo } catch { /* catch-mudo: sólo se espera el fin; el resultado lo informa reenviarVerificacion */ }
            setLoading(false)
            return
          }
          try { await signOut(auth) } catch (e) { authLog.warn('signOut del candado de verificación', e?.message || e) }
          setFaltaVerificar(true)
          setError(MENSAJE_FALTA_VERIFICAR)
          setLoading(false)
          return
        }
        try {
          // Resolver userDoc primero (necesitamos saber si es cliente o consultor)
          let userData = await ensureUserDoc(firebaseUser)

          // Invitación por código pendiente (registro con contraseña, PR 3b):
          // el canje lo hace el SERVIDOR y exige el email verificado, así que
          // ocurre acá, en el primer login verificado. `aplicarInvitacionAlLogin`
          // (cliente, escribía rolId/tipo desde el navegador) se retiró.
          const codigoPendiente = leerInvitacionPendiente()
          let avisoInvitacion = null
          if (codigoPendiente && firebaseUser.emailVerified) {
            try {
              await llamarAceptarInvitacion(codigoPendiente)
              borrarInvitacionPendiente()
              const freshSnap = await getDoc(doc(db, COLLECTIONS.USUARIOS, firebaseUser.uid))
              if (freshSnap.exists()) userData = docConId(freshSnap)
            } catch (invErr) {
              // Un rechazo definitivo (código usado/vencido/ajeno) no se reintenta
              // en cada login; uno transitorio (red, App Check) sí.
              if (esRechazoDefinitivoDeInvitacion(invErr)) borrarInvitacionPendiente()
              authLog.warn('canje de invitación al login', invErr?.message || invErr)
              avisoInvitacion = mensajeDeInvitacion(invErr)
            }
          }

          // Staff Milbrands se decide por el FLAG canónico (tipo:'milbrands' /
          // wizard), NO por el dominio del email (C.1 / ADR 003 — alineado con
          // `esStaffServer` del backend). El flag solo lo asigna un admin y la
          // frontera real de autorización es el backend (rules + callables); por
          // eso ya no rechazamos a un staff con email externo: si tiene el flag,
          // entra. (Antes: bloqueo hardcodeado por '@milbrands.com.ar'.)

          // El candado de verificación vive arriba (A3): acá el token ya está
          // verificado. Si Firebase dice verificado pero el doc no, lo sincroniza el SERVIDOR
          // desde el token (PR 3a): el cliente ya no escribe `emailVerificado`.
          if (firebaseUser.emailVerified && userData.emailVerificado === false) {
            try {
              await llamarRegistrarUsuario({ nombre: userData.nombre || firebaseUser.displayName || firebaseUser.email })
              userData.emailVerificado = true
            } catch {}
          }

          // Fase 1.6 — Bloqueo por estado de empresa para cliente/colaborador.
          //   baja / baja_sin_cobro → logout inmediato con mensaje.
          //   suspension            → dejamos entrar pero marcamos el banner.
          if (userData.tipo === USER_TIPOS.CLIENTE || userData.tipo === USER_TIPOS.COLABORADOR) {
            try {
              const bloqueo = await estadoBloqueoPorEmpresa(userData)
              if (bloqueo.bloqueado) {
                await signOut(auth)
                setError(bloqueo.motivo || 'Tu cuenta fue dada de baja. Contactá al administrador.')
                setEstadoBloqueo({ bloqueado: false })
                setLoading(false)
                return
              }
              setEstadoBloqueo(bloqueo)
            } catch (e) {
              console.warn('[AuthContext] estadoBloqueoPorEmpresa falló, dejamos pasar:', e)
              setEstadoBloqueo({ bloqueado: false })
            }
          } else {
            setEstadoBloqueo({ bloqueado: false })
          }

          setUser(firebaseUser)
          setUserDoc(userData)

          // ── v2 — Cargar rol + asignaciones en cache de sesión ─────
          const v2 = await loadRolYAsignacionesV2(userData)
          setRolDoc(v2.rol)
          setAsignacionesV2(v2.asignaciones)

          // #87 — Cache de ejes /modulos/{codigo}.eje (no bloqueante).
          // Si falla (rules, red), ejeDe() cae a MODULO_EJE_FALLBACK (código).
          cargarEjesEnCache().catch(e =>
            console.warn('[AuthContext] cargarEjesEnCache falló:', e?.message))

          // El login salió bien; si el canje de la invitación no, el aviso queda.
          setError(avisoInvitacion)
        } catch (err) {
          if (err?.message === '__milbrands_no_doc__') {
            setError('Tu cuenta @milbrands.com.ar no está habilitada en el sistema. Contactá al administrador.')
          } else if (err?.message === '__no_user_doc__') {
            setError('No existe una cuenta asociada. Si sos cliente, registrate primero.')
          } else {
            console.error('Error loading user:', err)
            setError('Error al cargar datos del usuario')
          }
        }
      } else {
        setUser(null)
        setUserDoc(null)
        setRolDoc(null)
        setAsignacionesV2([])
        setEstadoBloqueo({ bloqueado: false })
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  // Crea/resuelve el doc de usuario. Para consultores conserva toda la
  // lógica previa (migración por email + first-user-admin).
  async function ensureUserDoc(firebaseUser) {
    // Si hay un canje de invitación en curso (Google o email+pass), esperamos
    // que termine antes de leer. Sino corremos el riesgo de no encontrar el
    // userDoc cliente y caer al fallback de "crear consultor".
    if (_registrationInFlight) {
      try { await _registrationInFlight } catch {}
    }

    const ref = doc(db, COLLECTIONS.USUARIOS, firebaseUser.uid)
    let snap = await getDoc(ref)

    // Race-condition guard adicional para email+pass: registerWithInvite
    // hace setDoc DESPUÉS del createUser, y onAuthStateChanged puede
    // dispararse en el medio. Reintentamos unas veces antes de fallback.
    if (!snap.exists()) {
      for (let i = 0; i < 5 && !snap.exists(); i++) {
        await new Promise(r => setTimeout(r, 400))
        snap = await getDoc(ref)
      }
    }

    if (snap.exists()) {
      const data = snap.data()
      // Sin `tipo`, la cuenta NO es staff: es lo que lee el servidor (`esStaff`,
      // `tipo === 'milbrands'`). Hasta la contención 2a del gate de identidad
      // (30-sep-2026) el login le ESCRIBÍA `USER_TIPOS.CONSULTOR` —que vale
      // 'milbrands'—, o sea que ascendía a staff a toda cuenta con rol de
      // consultor y sin tipo. El navegador ya no escribe `tipo` en el doc propio
      // (las reglas no lo dejan): el default vive sólo en memoria y falla cerrado.
      const tipo = data.tipo || USER_TIPOS.CLIENTE
      // Sincronizar foto con Google cada vez que entran: así queda al día en
      // Firestore y la ven el resto (consultores, equipo, etc.). Best-effort: que
      // no se pueda escribir (p. ej. con la plataforma en sólo-lectura) no tumba
      // el login.
      if (firebaseUser.photoURL && data.foto !== firebaseUser.photoURL) {
        try {
          await updateDoc(ref, { foto: firebaseUser.photoURL, actualizadoEn: serverTimestamp() })
        } catch (e) {
          authLog.warn('no se pudo sincronizar la foto', e?.code || e?.message || e)
        }
      }
      // El id es el del DOCUMENTO (`docConId`): un campo `id` guardado no lo pisa.
      return {
        ...docConId(snap),
        tipo,
        foto: firebaseUser.photoURL || data.foto,
      }
    }

    // No encontrado por UID → buscar pre-registrado por email
    const emailQuery = query(
      collection(db, COLLECTIONS.USUARIOS),
      where('email', '==', firebaseUser.email)
    )
    const emailSnap = await getDocs(emailQuery)

    if (!emailSnap.empty) {
      const oldDoc = emailSnap.docs[0]
      const oldId = oldDoc.id
      const oldData = oldDoc.data()

      // Usuario pre-creado por admin con método Google (doc-only, id autogenerado):
      // el re-key (doc + asignaciones + accesos → uid de Google) lo hace el backend
      // con Admin SDK (vincularCuentaGoogle), porque el cliente no puede migrar
      // asignaciones (rule staff-only). Preserva rol/permisos/empresas.
      if (oldData.metodoAuth === 'google' || oldData.estado === 'invitado_google') {
        // En el PRIMER login Google la lectura del doc re-keado puede llegar antes
        // de que el re-key server-side propague (timing de App Check / consistencia),
        // o el primer callable puede fallar porque el token de App Check todavía no
        // está listo. Sin reintento, el usuario nuevo cae al fallback, ve un error y
        // tiene que loguearse DOS veces. Reintentamos unas veces antes de caer al
        // merge legacy. vincularCuentaGoogle es idempotente (devuelve 'ya_vinculado').
        for (let intento = 0; intento < 5; intento++) {
          try {
            await httpsCallable(functions, 'vincularCuentaGoogle')()
            const fresh = await getDoc(doc(db, COLLECTIONS.USUARIOS, firebaseUser.uid))
            if (fresh.exists()) return docConId(fresh)
          } catch (e) {
            // Transitorio (token App Check no listo, red): reintentamos. Si persiste
            // tras los reintentos, cae al merge legacy de abajo (fallback).
            authLog.warn('vincularCuentaGoogle reintento', intento + 1, e?.message || e)
          }
          await new Promise(r => setTimeout(r, 500))
        }
      }

      // GUARDA: si el login entró por Google (dominio @milbrands.com.ar) y el
      // doc matcheado por email es tipo='cliente' (ej. un usuario de prueba
      // "_test_cliente_*" o un contacto convertido a cliente), NO fusionamos.
      // Eso degradaría al consultor a cliente. En cambio, ignoramos ese doc
      // y seguimos al flujo de "crear consultor nuevo".
      const esGoogleMilbrands = firebaseUser.email?.endsWith('@' + ALLOWED_DOMAIN)
      const oldEsCliente = oldData.tipo === USER_TIPOS.CLIENTE
      if (esGoogleMilbrands && oldEsCliente) {
        console.warn(
          `[ensureUserDoc] Ignorando doc cliente pre-existente con email ` +
          `${firebaseUser.email} (id=${oldId}): el login es consultor Google. ` +
          `Para evitar el conflicto cambiá el email de ese doc o borralo.`
        )
        // Caer al bloque de "crear consultor nuevo" más abajo.
      } else {
        const mergedTipo = oldData.tipo || USER_TIPOS.CONSULTOR
        const mergedUser = {
          ...oldData,
          tipo: mergedTipo,
          nombre: oldData.nombre || firebaseUser.displayName || '',
          foto: firebaseUser.photoURL || oldData.foto || '',
          actualizadoEn: serverTimestamp(),
        }
        await setDoc(ref, mergedUser)

        // Migrar accesos al nuevo UID
        const accesosQuery = query(
          collection(db, COLLECTIONS.ACCESOS),
          where('userId', '==', oldId)
        )
        const accesosSnap = await getDocs(accesosQuery)
        const batch = writeBatch(db)
        accesosSnap.docs.forEach(aDoc => {
          batch.update(doc(db, COLLECTIONS.ACCESOS, aDoc.id), { userId: firebaseUser.uid })
        })
        batch.delete(doc(db, COLLECTIONS.USUARIOS, oldId))
        await batch.commit()

        return { id: firebaseUser.uid, ...mergedUser }
      }
    }

    // No hay doc pre-existente.
    // Cuentas @milbrands.com.ar DEBEN ser creadas por el admin previamente.
    // Si no existe el doc, rechazamos el login.
    if (firebaseUser.email?.endsWith('@' + ALLOWED_DOMAIN)) {
      await signOut(auth)
      throw new Error('__milbrands_no_doc__')
    }

    // Una cuenta de EMAIL+PASSWORD sin doc sólo puede venir de un registro
    // (o invitación) cuyo callable falló después de crear el usuario en Auth:
    // el servidor lo repara (idempotente) antes de rechazar el login. Una
    // cuenta de Google sin doc NO se auto-crea: los clientes se crean vía
    // /registro o invitación (PR 3a, H3b).
    if (esCuentaDePassword(firebaseUser)) {
      try {
        await llamarRegistrarUsuario({ nombre: firebaseUser.displayName || firebaseUser.email })
        const reparado = await getDoc(ref)
        if (reparado.exists()) return docConId(reparado)
      } catch (e) {
        authLog.warn('reparación del registro falló', e?.message || e)
      }
    }
    await signOut(auth)
    throw new Error('__no_user_doc__')
  }

  // Cargar usuario impersonado (solo si el user real es system admin).
  // Funciona tanto en dev como en producción, pero únicamente super admin
  // puede activarlo (el link sólo se muestra para admins).
  useEffect(() => {
    if (!impersonateUid || !user || !userDoc) { setImpersonated(null); return }
    // v2 (#49 B9.4-b) — sólo Wizard puede impersonar: `puedeImpersonar` (una
    // fuente con VerComoButton). Antes `|| tipo === 'milbrands'` ampliaba a todo staff.
    if (!puedeImpersonar(userDoc)) { setImpersonated(null); return }
    let active = true
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, COLLECTIONS.USUARIOS, impersonateUid))
        if (!active) return
        if (snap.exists()) {
          const data = docConId(snap)
          setImpersonated({ userDoc: data })
        } else {
          setImpersonated(null)
        }
      } catch (e) {
        console.error('Error cargando impersonación:', e)
        setImpersonated(null)
      }
    })()
    return () => { active = false }
  }, [impersonateUid, user?.uid, userDoc?.id, userDoc?.esWizard, userDoc?.tipo])

  function stopImpersonating() {
    try { window.sessionStorage.removeItem('mb_impersonate_uid') } catch { /* noop */ }
    const url = new URL(window.location.href)
    url.searchParams.delete('impersonate')
    window.location.href = url.toString()
  }

  // Getters "efectivos" que consideran impersonación + vista simulada
  const baseUserDoc = impersonated?.userDoc || userDoc
  const isImpersonating = !!impersonated

  // Rol simulado (#58): sobreescribe rolId (+ tipo / empresaIds) del userDoc
  // efectivo. El sidebar (useModulosVisibles) y puede() re-derivan del rolId
  // simulado. El alcance de empresas real no cambia salvo simulación de cliente.
  const effectiveUserDoc = useMemo(() => {
    if (!rolSimulado || !baseUserDoc) return baseUserDoc
    const override = { ...baseUserDoc, rolId: rolSimulado.rolId }
    if (rolSimulado.tipo) override.tipo = rolSimulado.tipo
    // Al simular un rol que no es Wizard, el flag derivado esWizard no aplica.
    if (rolSimulado.rolId !== 'WIZARD') override.esWizard = false
    // Simular cliente: además del rol, scope a una empresa concreta.
    if (rolSimulado.tipo === USER_TIPOS.CLIENTE && rolSimulado.empresaId) {
      override.empresaIds = [rolSimulado.empresaId]
    }
    return override
  }, [baseUserDoc, rolSimulado])

  // ── v2 — Cargar rol del usuario + sus asignaciones activas ─────────
  // Se ejecuta al login (post ensureUserDoc) y se cachea en sesión.
  // Si el usuario no tiene rolId todavía (caso pre-migración B3), retorna
  // { rol: null, asignaciones: [] } y los helpers caen al comportamiento legacy.
  async function loadRolYAsignacionesV2(usuarioDoc) {
    if (!usuarioDoc) return { rol: null, asignaciones: [] }
    // La identidad es el id del DOCUMENTO, nunca su campo `uid` (que el
    // navegador escribía): con `uid` primero, una cuenta cargaba las
    // asignaciones de otra y la UI la trataba como a esa otra.
    const uid = usuarioDoc.id
    if (!uid) return { rol: null, asignaciones: [] }

    // Cargar asignaciones del usuario (siempre, aunque no tenga rolId).
    let asignaciones = []
    try {
      const qa = query(
        collection(db, COLLECTIONS.ASIGNACIONES),
        where('userId', '==', uid),
        where('esActivo', '==', true),
      )
      const sa = await getDocs(qa)
      asignaciones = sa.docs.map(docConId)
    } catch (e) {
      console.warn('[AuthContext] no se pudieron cargar /asignaciones del usuario:', e?.message || e)
    }

    // Cargar rol del usuario por rolId. Si no existe, retornamos rol=null.
    let rol = null
    if (usuarioDoc.rolId) {
      try {
        // El rolId guardado en /usuarios es el `codigo` del rol (string mayúscula)
        // que también es el doc.id en /roles según seed v2.
        const ref = doc(db, COLLECTIONS.ROLES, usuarioDoc.rolId)
        const snap = await getDoc(ref)
        if (snap.exists()) {
          rol = docConId(snap)
        } else {
          // Fallback: buscar por campo `codigo` (por si en algún momento se desincronizan)
          const qr = query(
            collection(db, COLLECTIONS.ROLES),
            where('codigo', '==', usuarioDoc.rolId),
          )
          const sr = await getDocs(qr)
          if (!sr.empty) {
            const d0 = sr.docs[0]
            rol = docConId(d0)
          }
        }
      } catch (e) {
        console.warn('[AuthContext] no se pudo cargar /roles del usuario:', e?.message || e)
      }
    }

    return { rol, asignaciones }
  }

  // rolDoc + asignaciones siguen a la identidad EFECTIVA (impersonado o real,
  // con override de rolSimulado).
  //  - rolSimulado (RoleSwitcher): baseUserDoc = el real → loadRol... consulta
  //    asignaciones por el uid REAL, así el alcance NO se simula (diseño previo).
  //  - Impersonación: baseUserDoc = usuario impersonado → carga SU rol y SUS
  //    asignaciones. Sin esto, esWizard()/empresasAlcance()/puede() seguían
  //    usando las asignaciones del admin y el portal filtraba mal (el Owner veía
  //    TODOS los usuarios del sistema, incluido staff Milbrands — bug real).
  useEffect(() => {
    if (!baseUserDoc) return
    let cancel = false
    const targetRolId = rolSimulado?.rolId || baseUserDoc.rolId || null
    loadRolYAsignacionesV2({ ...baseUserDoc, rolId: targetRolId })
      .then(v2 => {
        if (cancel) return
        setRolDoc(v2.rol)
        setAsignacionesV2(v2.asignaciones)
      })
      .catch(() => { /* el login flow ya dejó un rolDoc base */ })
    return () => { cancel = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUserDoc?.id, rolSimulado?.rolId])

  // Login Google. El dominio `hd` sale de la config (configuracion/auth), NO
  // hardcodeado: 'solo-milbrands' → restringe al dominio Workspace; 'todos' → sin
  // restricción. LoginPage pasa la config (useAuthConfig); fallback al default.
  async function login(authConfig) {
    setError(null)
    setFaltaVerificar(false)
    try {
      // Race fix: si la config todavía no terminó de cargar (el usuario clickeó
      // "Continuar con Google" antes de que useAuthConfig leyera configuracion/auth),
      // la leemos fresca acá. Sin esto se usa el default 'solo-milbrands' → se
      // aplica hd=ALLOWED_DOMAIN y se fuerza @dominio en el primer intento, lo que
      // obliga a loguear dos veces.
      let cfg = authConfig
      if (!cfg || cfg.loaded !== true) {
        try {
          const snap = await getDoc(doc(db, 'configuracion', 'auth'))
          cfg = normalizarAuthConfig(snap.exists() ? snap.data() : {})
        } catch {
          cfg = authConfig || AUTH_CONFIG_DEFAULT
        }
      }
      const hd = hdParaGoogle(cfg || AUTH_CONFIG_DEFAULT)
      googleProvider.setCustomParameters(hd ? { hd } : {})
      await signInWithPopup(auth, googleProvider)
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        // SEC-F0.3 — usuario con 2FA: capturamos el resolver y dejamos que el UI
        // de login pida el código TOTP. submitMfaCode() completa el sign-in.
        try {
          const resolver = getMultiFactorResolver(auth, err)
          setMfaResolver(resolver)
        } catch (e) {
          console.error('[AuthContext] mfa resolver error', e)
          setError('Error preparando el segundo factor')
        }
        throw err
      }
      // El popup de Google puede fallar por config del proyecto staging/prod
      // (auth/unauthorized-domain, auth/operation-not-allowed), por el browser
      // del usuario (popup bloqueado, cookies de terceros, red) o porque el
      // usuario cerró la ventana. Mapeamos a mensaje legible dejando el code
      // visible para diagnóstico remoto.
      const msg = mensajeAuthError(err)
      if (msg) {
        setError(msg)
        authLog.error('login Google falló', err, { code: err?.code })
      }
      throw err
    }
  }

  // Login email/password (clientes)
  async function loginEmail(email, password) {
    setError(null)
    setFaltaVerificar(false)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        // SEC-F0.3 — mismo flujo que en login Google.
        try {
          const resolver = getMultiFactorResolver(auth, err)
          setMfaResolver(resolver)
        } catch (e) {
          console.error('[AuthContext] mfa resolver error', e)
          setError('Error preparando el segundo factor')
        }
        throw err
      }
      const msg = mensajeAuthError(err) || 'No se pudo iniciar sesión.'
      setError(msg)
      authLog.error('login email falló', err, { code: err?.code })
      throw err
    }
  }

  // SEC-F0.3 — Completar login cuando el usuario tiene MFA enrollado.
  // El resolver expone `hints` (factores enrollados) y `resolveSignIn()`.
  // Hoy solo soportamos TOTP; para SMS habría que agregar PhoneAuthProvider.
  async function submitMfaCode(code, hintIndex = 0) {
    if (!mfaResolver) throw new Error('No hay un proceso de 2FA en curso')
    setError(null)
    const hint = mfaResolver.hints[hintIndex]
    if (!hint || hint.factorId !== TotpMultiFactorGenerator.FACTOR_ID) {
      const msg = 'Solo soportamos códigos TOTP por ahora'
      setError(msg)
      throw new Error(msg)
    }
    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code)
      await mfaResolver.resolveSignIn(assertion)
      setMfaResolver(null) // limpia state — onAuthStateChanged completa el flow
    } catch (err) {
      const msg = err.code === 'auth/invalid-verification-code'
        ? 'Código incorrecto. Probá con el código actual de tu app.'
        : 'No se pudo verificar el código'
      setError(msg)
      throw err
    }
  }

  function cancelMfa() {
    setMfaResolver(null)
    setError(null)
  }

  // Registro de cliente mediante código de invitación.
  // Busca la invitación, valida, crea Firebase Auth user y crea userDoc tipo cliente.
  // Activación con código + email/contraseña (PR 3b). El canje lo hace el
  // servidor y exige el email verificado: acá sólo se crea la cuenta, el doc
  // base (`registrarUsuario`) y se guarda el código; el canje ocurre en el
  // primer login verificado (bloque de `onAuthStateChanged`). El navegador ya
  // no lee `invitaciones` (la rule sólo deja leer por email al invitado).
  async function registerWithInvite({ codigo, email, password, nombre }) {
    setError(null)
    const normalized = (codigo || '').trim().toUpperCase()
    if (!normalized) throw new Error('Código requerido')
    guardarInvitacionPendiente(normalized)
    await registrarConEmail({ nombre: nombre || '', empresaNombre: '', email: (email || '').toLowerCase().trim(), password })
    return { pendienteVerificacion: true }
  }

  // Registro de cliente vía código + Google (sin contraseña).
  // El email del Google determina la cuenta. Si la invitación tiene email,
  // debe coincidir con el del Google.
  async function registerWithInviteGoogle({ codigo }) {
    setError(null)
    const normalized = (codigo || '').trim().toUpperCase()
    if (!normalized) throw new Error('Código requerido')

    // Flag in-flight ANTES del popup: `ensureUserDoc` espera al callable, que
    // es el que crea el doc (una cuenta de Google sin doc, si no, se rechaza).
    let resolveFlight, rejectFlight
    _registrationInFlight = new Promise((res, rej) => { resolveFlight = res; rejectFlight = rej })
    // Si nadie está esperando el flag (ensureUserDoc todavía no corrió), el
    // rechazo no puede quedar como promesa sin manejar: el error real sube por
    // el `throw` de abajo.
    _registrationInFlight.catch(() => {})
    try {
      const cred = await signInWithPopup(auth, googleProvider)
      try {
        await llamarAceptarInvitacion(normalized)
      } catch (err) {
        // Sin canje no hay doc: se cierra la sesión para no dejar una cuenta
        // de Google a medio activar.
        setError(mensajeDeInvitacion(err))
        try { await signOut(auth) } catch (e) { authLog.warn('signOut tras canje fallido', e?.message || e) }
        throw err
      }
      resolveFlight()
      return cred.user
    } catch (err) {
      if (err?.code?.startsWith('auth/')) {
        const msg = mensajeAuthError(err)
        if (msg) {
          setError(msg)
          authLog.error('activar con Google falló', err, { code: err?.code })
        }
      }
      rejectFlight(err)
      throw err
    } finally {
      _registrationInFlight = null
    }
  }

  // ── Auto-registro (RegistroPage) — el doc lo escribe el servidor (PR 3a) ──
  // El flag in-flight va ANTES de crear/loguear en Auth: `onAuthStateChanged`
  // dispara en ese momento y `ensureUserDoc` tiene que esperar al callable.
  async function registrarConEmail({ nombre, empresaNombre, email, password, volverA = null }) {
    let resolveFlight
    _registrationInFlight = new Promise((res) => { resolveFlight = res })
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password)
      // El displayName es cosmético: si falla no frena el registro.
      try { await updateProfile(cred.user, { displayName: nombre }) } catch (e) { authLog.warn('updateProfile en registro', e?.message || e) }
      await llamarRegistrarUsuario({ nombre, empresaNombre: empresaNombre || '', telefono: '' })
      // El mail sale DESPUÉS de que el doc existe: si el callable falló, el
      // reintento es el login (ensureUserDoc repara) y ahí se reenvía.
      // `volverA` (Paso 6): el link del mail vuelve a esa ruta INTERNA (el
      // link de una entrada regalada) en vez de a la raíz; sólo rutas propias,
      // que empiezan con una barra, y el origen lo pone el navegador.
      const destino = destinoDeVerificacion(volverA)
      await sendEmailVerification(cred.user, destino ? { url: destino } : undefined)
      await signOut(auth)
    } finally {
      resolveFlight()
      _registrationInFlight = null
    }
  }

  async function registrarConGoogle({ empresaNombre }) {
    let resolveFlight
    _registrationInFlight = new Promise((res) => { resolveFlight = res })
    try {
      const cred = await signInWithPopup(auth, googleProvider)
      await llamarRegistrarUsuario({ nombre: cred.user.displayName || cred.user.email, empresaNombre: empresaNombre || '', telefono: '' })
      // Queda logueado → ensureUserDoc encuentra el doc recién creado.
    } finally {
      resolveFlight()
      _registrationInFlight = null
    }
  }

  // A3 — el link del mail vuelve a `/login` (continueUrl) pasando por la
  // pantalla propia `/auth/accion`. Anti-enumeración: el resultado para quien
  // pidió el mail es el MISMO exista o no la cuenta (`auth/user-not-found`,
  // si el proyecto no tiene la protección de enumeración, se traga acá).
  async function resetPassword(email) {
    setError(null)
    const origen = typeof window !== 'undefined' && window.location ? window.location.origin : ''
    const ajustes = origen ? { url: `${origen}/login` } : undefined
    try {
      try {
        await sendPasswordResetEmail(auth, email, ajustes)
      } catch (err) {
        // Un origen que no está en los dominios autorizados (un canal de
        // preview) no puede dejar a nadie sin reset: sale sin continueUrl.
        if (!ajustes || (err?.code !== 'auth/unauthorized-continue-uri' && err?.code !== 'auth/invalid-continue-uri')) throw err
        authLog.warn('continueUrl del reset rechazado, sale sin él', err?.code)
        await sendPasswordResetEmail(auth, email)
      }
    } catch (err) {
      if (err?.code === 'auth/user-not-found') return
      setError(err?.code === 'auth/invalid-email' || err?.code === 'auth/too-many-requests' || err?.code === 'auth/network-request-failed'
        ? mensajeAuthError(err)
        : 'Error al enviar el correo de recuperación')
      throw err
    }
  }

  // A3 — reenviar el mail de verificación a quien el candado dejó afuera. Con
  // la contraseña del form (sin ella no hay sesión para mandarlo, y así nadie
  // dispara mails a una casilla ajena), uno a la vez y con espera entre
  // envíos. Devuelve { enviado, motivo?, mensaje } para que el login lo diga.
  async function reenviarVerificacion(email, password, volverA = null) {
    if (_reenvioEnVuelo) return { enviado: false, motivo: 'en-curso', mensaje: 'Ya estamos reenviando el mail.' }
    const restante = _ultimoReenvioMs + ESPERA_ENTRE_REENVIOS_MS - Date.now()
    if (_ultimoReenvioMs && restante > 0) {
      const segundos = Math.ceil(restante / 1000)
      return { enviado: false, motivo: 'espera', segundos, mensaje: `Esperá ${segundos} s antes de pedir otro mail.` }
    }
    let terminar
    _reenvioEnVuelo = new Promise((res) => { terminar = res })
    let sesionAbierta = false
    try {
      const cred = await signInWithEmailAndPassword(auth, (email || '').trim(), password || '')
      sesionAbierta = true
      if (cred.user.emailVerified) {
        // Ya verificó (en otra pestaña, o recién): el login sigue su curso.
        sesionAbierta = false
        setFaltaVerificar(false)
        setError(null)
        return { enviado: false, motivo: 'ya-verificado', mensaje: 'Tu email ya está verificado. Entrando…' }
      }
      const destino = destinoDeVerificacion(volverA)
      _ultimoReenvioMs = Date.now()
      await sendEmailVerification(cred.user, destino ? { url: destino } : undefined)
      return { enviado: true, mensaje: 'Te reenviamos el mail de verificación. Revisá tu bandeja de entrada (y la carpeta de spam).' }
    } catch (err) {
      if (err?.code === 'auth/too-many-requests') {
        _ultimoReenvioMs = Date.now()
        return { enviado: false, motivo: 'demasiados', mensaje: 'Pediste demasiados mails seguidos. Esperá unos minutos y volvé a intentar.' }
      }
      authLog.warn('reenvío de verificación falló', err?.code || err?.message)
      return { enviado: false, motivo: 'error', mensaje: mensajeAuthError(err) || 'No se pudo reenviar el mail.' }
    } finally {
      if (sesionAbierta) { try { await signOut(auth) } catch (e) { authLog.warn('signOut tras reenvío', e?.message || e) } }
      _reenvioEnVuelo = null
      terminar()
    }
  }

  async function logout() {
    // #88 — vaciamos caches de sesión antes del signOut para que no queden
    // nombres de la sesión anterior si otro user se loguea en el mismo browser.
    try { limpiarCachesNombres() } catch { /* noop */ }
    // Mismo motivo para el directorio por empresa (report du3xh8vn8Gmd8fmmf5aB):
    // es un cache de sesión y no puede sobrevivir al cambio de usuario.
    try { limpiarCacheDirectorioEmpresa() } catch { /* catch-mudo: vaciar un Map en memoria no puede impedir el signOut — si fallara, el logout igual tiene que salir */ }
    await signOut(auth)
  }

  /**
   * ¿Esta cuenta ve TODAS las empresas? — la pregunta de ALCANCE.
   *
   * CUÁL DE LAS TRES RESPUESTAS DEL SERVER ES ÉSTA, porque son tres y no dos
   * (report `dJjXW1t4fpbtFY3EXi8n`):
   *   1. PERMISO — `puedeCon` (`functions/permisos.js:534`) bypasea la matriz
   *      sólo por `esWizardDe` (`:281`): sin OPERACIONES y sin `sistemaTotal`.
   *      Para esa pregunta el espejo es `esWizard()`, no esto.
   *   2. ALCANCE DE DATOS — `empresasAlcanceDe` (`:547`) devuelve `'*'` por
   *      `esWizardDe` Y por `scope.sistemaTotal === true` (`:556`), pero NO por
   *      OPERACIONES.
   *   3. LECTURA DEL CATÁLOGO de empresas — `firestore.rules:1475`:
   *      `allow read: if isConsultor() || perteneceAEmpresa(eId)`, o sea
   *      cualquier staff.
   *
   * **Esto NO es espejo de `empresasAlcanceDe`, y no hay que "arreglarlo"
   * angostándolo.** Incluye OPERACIONES a propósito: el techo real de lo que
   * se puede leer es (3), y la decisión de UI de mostrarle todo a Wizard y
   * Operaciones es la del bug #75, escrita en `src/core/lib/alcanceEmpresas.js`.
   * Angostarlo al espejo literal de (2) le dejaría a OPERACIONES pantallas
   * vacías sin motivo visible, contradiciendo al server en vez de seguirlo.
   */
  function veTodasLasEmpresas() {
    // Con rol simulado, el admin pierde privilegios de admin salvo que el rol
    // simulado sea él mismo un rol admin global (Wizard / Operaciones).
    // (La simulación no debe dar MÁS poder, solo cambiar la perspectiva.)
    if (rolSimulado && !ROLES_ADMIN_GLOBAL.includes(rolSimulado.rolId)) return false
    // v2: admin = WIZARD o OPERACIONES, o tiene asignación con scope.wizard / sistemaTotal.
    if (effectiveUserDoc?.esWizard === true) return true
    const rolCodigo = effectiveUserDoc?.rolId
    if (rolCodigo && ROLES_ADMIN_GLOBAL.includes(rolCodigo)) return true
    // Asignación directa wizard/sistemaTotal (puede no estar reflejada en esWizard derivado)
    if (asignacionesV2.some(a => a?.scope?.wizard === true || a?.scope?.sistemaTotal === true)) return true
    return false
  }

  /**
   * @deprecated Contesta DOS preguntas con una sola respuesta, y el server las
   * tiene separadas (report `dJjXW1t4fpbtFY3EXi8n`).
   *
   * - Para **alcance de empresas** («¿ve todas?») usá `veTodasLasEmpresas()`,
   *   que es dueña del cuerpo: esto delega en ella y no tiene puerta propia.
   * - Para **`modulo.accion`** NO hay reemplazo todavía, y por eso esto sigue
   *   vivo: el camino es `puedo(modulo, accion)` de `src/core/hooks/usePermisos.js`,
   *   que es donde vive el flag `feature.permisos.bypass-solo-wizard` que
   *   angosta la rama ancha. Esa migración tiene precondición escrita
   *   (`usePermisos.js`) y no se hace sitio por sitio desde acá.
   *
   * Delega en vez de copiar el cuerpo a propósito: dos cuerpos idénticos
   * divergen, y el ancla de `usePermisos.paridad.test.js` extrae POR NOMBRE —
   * con una copia quedaría cuidando a éste, que se va a morir, y no al que
   * decide alcance.
   */
  function isAdmin() {
    return veTodasLasEmpresas()
  }

  // El admin "real" (no afectado por impersonación) — se usa para decidir
  // si mostramos controles de dev como "Ver como cliente".
  function isRealAdmin() {
    const realDoc = impersonated?.userDoc || userDoc
    if (realDoc?.esWizard === true) return true
    if (realDoc?.rolId && ROLES_ADMIN_GLOBAL.includes(realDoc.rolId)) return true
    return false
  }

  function isCliente() {
    // La frontera sale del CATÁLOGO (`roles/{id}.lado`) vía esRolLadoMilbrands;
    // la lista de 4 quedó como fallback para roles sin lado declarado (caso
    // DDA: rol "Analista de Producto y Calidad" caía al portal por no estar
    // en la lista). El helper solo usa rolDoc si es EL doc del rol evaluado.
    if (rolSimulado) return !esRolLadoMilbrands(rolSimulado.rolId, rolDoc)
    const rolCodigo = effectiveUserDoc?.rolId
    if (rolCodigo) return !esRolLadoMilbrands(rolCodigo, rolDoc)
    // Fallback legacy
    return effectiveUserDoc?.tipo === USER_TIPOS.CLIENTE
  }

  // El cliente "real" (no afectado por vista simulada) — se usa en route guards
  // para no redirigir al admin al portal cuando está simulando vista cliente.
  function isRealCliente() {
    const real = impersonated?.userDoc || userDoc
    // rolDoc puede ser el del rol SIMULADO — esRolLadoMilbrands lo ignora si
    // su codigo no coincide con el rol real evaluado (guard del helper).
    if (real?.rolId) return !esRolLadoMilbrands(real.rolId, rolDoc)
    return real?.tipo === USER_TIPOS.CLIENTE
  }

  function isConsultor() {
    // Misma frontera que isCliente, por el mismo helper — dos copias divergen.
    if (rolSimulado) return esRolLadoMilbrands(rolSimulado.rolId, rolDoc)
    const rolCodigo = effectiveUserDoc?.rolId
    if (rolCodigo) return esRolLadoMilbrands(rolCodigo, rolDoc)
    // Fallback legacy: cualquier user que NO sea cliente
    return !effectiveUserDoc || effectiveUserDoc.tipo !== USER_TIPOS.CLIENTE
  }

  // v2 — atajo directo para Wizard (root).
  function esWizard() {
    // Si se está simulando un rol que no es Wizard, no se filtran poderes Wizard.
    if (rolSimulado && rolSimulado.rolId !== 'WIZARD') return false
    return effectiveUserDoc?.esWizard === true
        || effectiveUserDoc?.rolId === 'WIZARD'
        || asignacionesV2.some(a => a?.scope?.wizard === true)
  }

  // v2 (#49 B9.4-b) — hasAccess() y hasModuleAccess() removidas.
  // Usar puede() para chequeos de permisos UI, o empresaIds del userDoc para alcance.

  // ── v2 — Helper sync para evaluar (modulo, accion) sobre un recurso opcional ─
  // Funciona contra el cache local (rolDoc + asignacionesV2). NO toca Firestore.
  // Para casos donde se necesite chequeo async (ej. cloud functions), usar permisos.js
  // directamente — esta función es para componentes UI que renderizan condicionalmente.
  function puede(modulo, accion, recurso = null) {
    if (esWizard()) return true
    if (!rolDoc) return false
    if (rolDoc.esActivo === false || rolDoc.activo === false) return false

    // Normalizar (acepta tanto formato canónico como legacy)
    const permisosNorm = (Array.isArray(rolDoc.permisos) ? rolDoc.permisos : [])
      .map(p => ({
        modulo: p.modulo || p.moduloCodigo,
        accion: p.accion || p.funcionCodigo,
        scope: p.scope || 'todo',
        filtros: p.filtros || null,
      }))
      .filter(p => p.modulo && p.accion)

    const matches = permisosNorm.filter(p => p.modulo === modulo && p.accion === accion)
    if (matches.length === 0) return false

    // Alcance de empresa: si el recurso tiene empresaId, validar que esté en alcance.
    if (recurso?.empresaId) {
      const enAlcance = (effectiveUserDoc?.empresaIds || []).includes(recurso.empresaId)
        || asignacionesV2.some(a => a?.scope?.sistemaTotal === true || a?.scope?.wizard === true)
      if (!enAlcance) return false
    }

    // Evaluar cada permiso match (OR entre permisos).
    // El scope por persona se mira con el id del DOCUMENTO (ver arriba).
    const uid = effectiveUserDoc?.id
    for (const p of matches) {
      // Scope
      let scopeOk = false
      if (p.scope === 'todo' || !p.scope) scopeOk = true
      else if (!recurso) scopeOk = false
      else if (p.scope === 'propio') {
        scopeOk = recurso.creadoPor === uid || recurso.ownerUserId === uid
      } else if (p.scope === 'asignado') {
        scopeOk = recurso.asignadoA === uid || recurso.responsableUserId === uid
      } else if (p.scope === 'involucrado' || p.scope === 'compartido') {
        const arr = recurso.participantesUserIds || recurso.participantes || recurso.visibleParaUserIds || []
        scopeOk = Array.isArray(arr) && arr.includes(uid)
      }
      if (!scopeOk) continue

      // Filtros declarativos (solo si hay recurso)
      if (p.filtros && recurso) {
        if (!recursoPasaFiltros(modulo, recurso, p.filtros)) continue
      }
      return true
    }
    return false
  }

  // ── v2 — Devuelve las empresas alcanzables (cache derivado) ────────
  function empresasAlcance() {
    if (esWizard()) return '*'
    if (asignacionesV2.some(a => a?.scope?.sistemaTotal === true)) return '*'
    return Array.isArray(effectiveUserDoc?.empresaIds) ? effectiveUserDoc.empresaIds : []
  }

  // ── Scope del permiso (ADR 020) — genérico para cualquier módulo ────
  // Devuelve el scope MÁS AMPLIO entre los permisos (modulo, accion) del rol
  // ('todo' > 'involucrado'/'compartido' > 'asignado' > 'propio'), o null si
  // no tiene el permiso. Wizard ⇒ 'todo'. Los módulos lo usan para acotar sus
  // QUERIES de listado (restricción de producto; la frontera sigue en rules).
  const SCOPE_ORDEN = ['propio', 'asignado', 'compartido', 'involucrado', 'todo']
  function scopePermiso(modulo, accion) {
    if (esWizard()) return 'todo'
    if (!rolDoc || rolDoc.esActivo === false || rolDoc.activo === false) return null
    const matches = (Array.isArray(rolDoc.permisos) ? rolDoc.permisos : [])
      .map(p => ({ modulo: p.modulo || p.moduloCodigo, accion: p.accion || p.funcionCodigo, scope: p.scope || 'todo' }))
      .filter(p => p.modulo === modulo && p.accion === accion)
    if (matches.length === 0) return null
    return matches.reduce((mejor, p) => (
      SCOPE_ORDEN.indexOf(p.scope) > SCOPE_ORDEN.indexOf(mejor) ? p.scope : mejor
    ), matches[0].scope)
  }

  // v2 (#49 B9.4) — etiqueta para el badge de UI. Ya NO lee /accesos legacy.
  // Para Wizard devuelve 'Wizard'. Para cliente, 'Cliente'. Para staff/usuarios
  // con rol, devuelve el `nombre` del rol (ej. "Owner Empresa", "Consultor").
  // Si todavía no se cargó rolDoc (early render) o el usuario no tiene rolId,
  // cae a 'Sin acceso' — debería ser transitorio.
  function getEtiqueta() {
    if (isCliente()) return 'Cliente'
    if (esWizard()) return 'Wizard'
    if (rolDoc?.nombre) return rolDoc.nombre
    return 'Sin acceso'
  }

  const value = {
    user,
    userDoc: effectiveUserDoc,
    // ── v2 — modelo formal expuesto al árbol React ───────────────
    rolDoc,                       // doc del rol cargado al login
    asignacionesV2,               // asignaciones activas del usuario (shape v2)
    puede,                        // sync — evalúa permiso contra cache local
    scopePermiso,                 // sync — scope más amplio del permiso (ADR 020)
    esWizard,                     // sync — usuario tiene asignación wizard
    empresasAlcance,              // sync — devuelve string[] | '*'
    loading,
    error,
    setError,
    login,              // Google (consultor)
    loginEmail,         // email+pass (cliente)
    registerWithInvite, // canje de código de invitación (email+pass)
    registerWithInviteGoogle, // canje de código + Google
    registrarConEmail,  // auto-registro email+pass — el doc lo escribe el servidor (PR 3a)
    registrarConGoogle, // auto-registro Google — ídem
    resetPassword,
    faltaVerificar,       // A3 — el último login rebotó por el candado de verificación
    reenviarVerificacion, // A3 — (email, password, volverA?) → { enviado, motivo?, mensaje }
    logout,
    veTodasLasEmpresas,
    isAdmin,            // @deprecated — delega en veTodasLasEmpresas (ver su JSDoc)
    isRealAdmin,
    isCliente,
    isRealCliente,
    isConsultor,
    getEtiqueta,
    // Impersonación (solo DEV)
    isImpersonating,
    stopImpersonating,
    realUserDoc: userDoc,
    // Rol simulado (role switcher para super admin)
    rolSimulado,
    setRolSimulado,
    // Fase 1.6 — estado de bloqueo / banner por estado de empresa (solo cliente/colaborador)
    estadoBloqueo,
    // SEC-F0.3 — MFA pendiente durante login. Si mfaResolver != null, el UI
    // debe mostrar prompt de código de 6 dígitos y llamar submitMfaCode().
    mfaResolver,
    submitMfaCode,
    cancelMfa,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
